/**
 * Desktop notifications for SecurOne broadcasts.
 *
 * Started once per session from the sidebar for accounts that can be
 * recipients (customer, department and basic users) - so it works anywhere in
 * SecurCloud, not only while the SecurOne page is open. It joins the socket
 * room of every customer the account belongs to; when the app backend inserts
 * a broadcast the server pings `securone.events.changed` with kind "raised".
 * The browser then asks the server whom to alert (GET /securone/notifications:
 * the recipients of the broadcast only, checked against the groups as they are
 * now - being able to read an event is not enough, see the customer report of
 * 2026-10-01) and shows a browser notification, a toast and the alarm sound.
 * The notification and the sound need the browser permission ("Slå på
 * notiser"); the toast does not. The sound loops like SecurArc's alarm centre
 * until the event is opened here, at most SOUND_MAX_MS. Browsers only let a
 * page start audio after the user has interacted with it (autoplay policy), so
 * the first click or key press on the page primes the element, and a play that
 * was blocked is retried on the next gesture.
 *
 * The console is still not a critical-alert surface: the phone is. This is a
 * heads-up on the laptop, and the notification opens the event's log.
 */
import Vue from "vue";
import store from "@/store";
import router from "@/router";
import i18n from "@/i18n";
import * as socket from "./securone.socket";
import api from "./securone.api";
import { POLL } from "./securone.constants";

const SOUND_KEY = "securone_alarm_sound";
const RECIPIENT_ROLES = ["superuser", "user", "securone_basic", "basic_user"];

let started = false;
let unsubscribe = null;
let unwatchUser = null;
let audio = null;
let audioPrimed = false;
let soundingEvent = null; // event id the sound is playing for
let soundTimer = null;
let pendingSound = null; // { eventId, at } - play() was blocked by the autoplay policy
const SOUND_MAX_MS = 30000;
const PENDING_SOUND_MAX_MS = 5 * 60 * 1000;
const notified = new Set();

export function permission() {
  return typeof Notification === "undefined" ? "unsupported" : Notification.permission;
}

export async function requestPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch (e) {
    return Notification.permission;
  }
}

export function soundEnabled() {
  try {
    return localStorage.getItem(SOUND_KEY) !== "off";
  } catch (e) {
    return true;
  }
}

export function setSoundEnabled(on) {
  try {
    localStorage.setItem(SOUND_KEY, on ? "on" : "off");
  } catch (e) {
    /* storage unavailable */
  }
}

function ensureAudio() {
  if (audio) return audio;
  audio = new Audio(require("@/assets/audios/emergency-alarm.mp3"));
  audio.preload = "auto";
  audio.loop = true;
  try {
    audio.load();
  } catch (e) {
    /* ignore */
  }
  return audio;
}

/**
 * Call from a user gesture (click, key press). A muted play/pause unlocks
 * later programmatic play for this document - the trick SecurArc's alarm
 * centre uses. Safe to call often.
 */
export function primeSound() {
  try {
    const a = ensureAudio();
    if (audioPrimed || soundingEvent) return;
    a.muted = true;
    const p = a.play();
    if (p && p.then) {
      p.then(() => {
        if (!soundingEvent) {
          a.pause();
          a.currentTime = 0;
        }
        a.muted = false;
        audioPrimed = true;
      }).catch(() => {
        a.muted = false;
      });
    }
  } catch (e) {
    /* no audio */
  }
}

function stopSound() {
  clearTimeout(soundTimer);
  soundTimer = null;
  soundingEvent = null;
  if (!audio) return;
  try {
    audio.pause();
    audio.currentTime = 0;
  } catch (e) {
    /* ignore */
  }
}

// The alarm sound needs the notifications switched on ("Slå på notiser" = the
// browser permission), like the Chrome notification: being a recipient alone
// rings nothing (2026-10-01). The toast in SecurCloud still shows either way.
function playSound(eventId) {
  if (!soundEnabled() || permission() !== "granted") return;
  try {
    const a = ensureAudio();
    a.muted = false;
    a.currentTime = 0;
    soundingEvent = eventId;
    pendingSound = null;
    const p = a.play();
    if (p && p.then) {
      p.catch(() => {
        // No user gesture on this document yet (typically right after a reload):
        // the browser refuses. Play as soon as the user touches the page.
        soundingEvent = null;
        pendingSound = { eventId, at: Date.now() };
      });
    }
    clearTimeout(soundTimer);
    soundTimer = setTimeout(stopSound, SOUND_MAX_MS);
  } catch (e) {
    /* no audio */
  }
}

// First interaction with the page: prime the element, or play the sound a
// blocked alarm is still waiting for (if the event has not been opened since).
function onGesture() {
  if (pendingSound) {
    const { eventId, at } = pendingSound;
    pendingSound = null;
    if (Date.now() - at < PENDING_SOUND_MAX_MS) {
      playSound(eventId);
      return;
    }
  }
  if (!audioPrimed) primeSound();
}

function open(eventId, customerId) {
  stopSound();
  try {
    window.focus();
  } catch (e) {
    /* ignore */
  }
  const target = { name: "securone_event", params: { id: eventId }, query: { org: customerId } };
  if (router.currentRoute.name !== "securone_event" || router.currentRoute.params.id !== eventId) router.push(target).catch(() => {});
}

function showNotification(title, body, eventId, customerId) {
  if (permission() !== "granted") return;
  try {
    const n = new Notification(title, { body, tag: `securone-${eventId}`, requireInteraction: true, icon: "/favicon.ico" });
    n.onclick = () => {
      open(eventId, customerId);
      n.close();
    };
  } catch (e) {
    /* Notification constructor unsupported (e.g. some mobile browsers) */
  }
}

function showToast(title, body, eventId, customerId) {
  if (!Vue.toasted) return;
  Vue.toasted.show(`${title} · ${body}`, {
    type: "error",
    theme: "outline",
    position: "top-right",
    duration: 20000,
    action: { text: i18n.t("securone.notifications.open"), class: "font-sans", onClick: (e, toast) => { toast.goAway(0); open(eventId, customerId); } },
  });
}


// The broadcasts this account is to be alerted about at one customer, as the
// server decides it (recipients only - never the sender, never someone who may
// merely read the event). null when the server could not be asked.
async function alertsFor(customerId) {
  try {
    return await api.notifications({ customerId });
  } catch (e) {
    return null;
  }
}

// A ping about an event we have not seen. Older socket processes send no
// `kind`; a resolved event is never news. Whether it is news for THIS account
// is the server's answer, not the ping's: every account of the customer gets it.
async function onEventsChanged(payload) {
  if (!payload || !payload.eventId) return;
  if (payload.status === "resolved" && String(payload.eventId) === soundingEvent) stopSound(); // resolved elsewhere: stop ringing
  if (payload.status === "resolved" || payload.kind === "updated") return;
  if (notified.has(String(payload.eventId))) return;
  const alerts = await alertsFor(payload.customerId);
  const event = (alerts || []).find((e) => String(e._id) === String(payload.eventId));
  if (event) notify(event, payload.customerId);
}

// event: a row of GET /securone/notifications
function notify(event, customerId) {
  const eventId = String(event._id);
  if (notified.has(eventId)) return;
  notified.add(eventId);
  const title = i18n.t("securone.notifications.title", { category: event.category ? event.category.name : "" });
  const body = [i18n.t("securone.notifications.sent_by", { name: event.raisedByName || "" }), event.departmentName].filter(Boolean).join(" · ");
  showNotification(title, body, eventId, customerId);
  showToast(title, body, eventId, customerId);
  playSound(eventId);
}

// Fallback that does not depend on the socket or on change streams: what the
// server says to alert about at each customer is compared with what we have seen.
// Asked again every round, so a change to the groups counts from the next one.
// This runs on every page in SecurCloud, so the interval matters: slow while
// the socket is connected and it is only a safety net, the old rate when it is
// the only thing that would notice (securone.constants).
let orgIds = [];
let pollTimer = null;
let primed = false;

async function poll() {
  for (const customerId of orgIds) {
    const alerts = await alertsFor(customerId);
    if (!alerts) continue; // customer not available right now
    for (const e of alerts) {
      if (primed) notify(e, customerId);
      else notified.add(String(e._id)); // alarms already running when we started are not news
    }
  }
  primed = true;
  pollTimer = setTimeout(poll, socket.isConnected() ? POLL.notificationsSocket : POLL.notificationsNoSocket);
}

/** Start listening for the signed-in account. Safe to call more than once. */
export async function start() {
  if (started) return;
  const user = store.state.user;
  if (!user || !RECIPIENT_ROLES.includes(user.role)) return;
  started = true;
  try {
    const context = await api.context();
    orgIds = (context.organisations || []).map((o) => String(o._id));
    socket.connect();
    orgIds.forEach((id) => socket.joinOrganisation(id));
    unsubscribe = socket.on("securone.events.changed", onEventsChanged);
    document.addEventListener("pointerdown", onGesture, true);
    document.addEventListener("keydown", onGesture, true);
    ensureAudio(); // fetch the file now, not when the alarm comes
    primed = false;
    clearTimeout(pollTimer);
    poll();
    // logout (store RESET) -> drop the rooms of this account; the next sign-in starts afresh
    if (!unwatchUser) {
      unwatchUser = store.watch((state) => state.user, (user) => {
        if (!user) {
          stop();
          socket.disconnect();
        }
      });
    }
  } catch (e) {
    // no SecurOne for this account (404) - nothing to listen for
    started = false;
  }
}

/**
 * The event is being handled here (raised from this desktop, or its screen was
 * opened): it is no longer news, and the sound for it stops.
 */
export function markSeen(eventId) {
  if (!eventId) return;
  notified.add(String(eventId));
  if (soundingEvent === String(eventId)) stopSound();
  if (pendingSound && pendingSound.eventId === String(eventId)) pendingSound = null;
}

export function stop() {
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
  document.removeEventListener("pointerdown", onGesture, true);
  document.removeEventListener("keydown", onGesture, true);
  stopSound();
  pendingSound = null;
  clearTimeout(pollTimer);
  pollTimer = null;
  orgIds = [];
  primed = false;
  notified.clear();
  started = false;
}
